/* eslint-disable max-lines-per-function */
/* eslint-disable indent */
/* eslint-disable new-cap */
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import readMore from 'Extensions/read-more';
import GTM from 'extensions/gtm';
import 'extensions/ui-components/x-badge';
import 'extensions/ui-components/x-banner';
import 'extensions/ui-components/x-card';
import 'extensions/ui-components/x-carousel';
import 'extensions/ui-components/x-checkbox';
import 'extensions/ui-components/x-loader';
import 'extensions/ui-components/x-product-card';
import 'extensions/ui-components/x-range-slider';
import 'extensions/ui-components/x-search-facet';
import 'extensions/ui-components/x-search-facet-title';
import 'extensions/ui-components/x-search-facet-value';
import 'extensions/ui-components/x-search-pagination';
import 'extensions/ui-components/x-search-results-count';
import 'extensions/ui-components/x-search-selected-facet';
import 'extensions/ui-components/x-select';
import 'extensions/ui-components/x-swatch';
import 'extensions/ui-components/x-toggle-details';
import {LazyLoadController, SalesBrandsController} from 'extensions/ui-controllers';
import {BreakpointLabels, BreakpointMixin} from 'extensions/ui-mixins';
import {defineCustomElement} from 'extensions/utils';
import {LitElement, html, nothing} from 'lit';
import {classMap} from 'lit/directives/class-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {when} from 'lit/directives/when.js';
import {SearchService, TrackingService} from '../../../services';
import stylesheet from './x-search-app.component.scss?as=stylesheet';

export class SearchApp extends MobxReactionUpdate(BreakpointMixin(LitElement)) {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			category: {
				type: String
			},
			currentDevice: {
				type: String
			},
			isDesktop: {
				state: true,
				type: Boolean
			},
			loading: {
				state: true,
				type: Boolean
			},
			query: {
				type: String
			},
			searchService: {
				state: true,
				type: Object
			},
			store: {
				state: true,
				type: Object
			},
			trackingService: {
				state: true,
				type: Object
			},
			url: {
				type: String
			}
		};
	}

	get visibleFacets () {
		return this.store?.state?.facets
			?.filter(facet => {
				// Remove non-visible facets
				if (!facet.visible) {
					return false;
				}

				// Filter facet if slider and min === max
				if (facet.type === 'range-slider') {
					return facet?.range?.min !== facet?.range?.max;
				}

				// Filter slider if values are empty
				return facet?.values?.length > 0;
			}) || [];
	}

	constructor () {
		super();

		// Create gtm helper
		this.gtm = new GTM(window.mivaJS);

		/**
		 * @type {TrackingService}
		 */
		this.trackingService = new TrackingService({
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID
		});

		/**
		 * @type {import('../../../services').SearchStore}
		 */
		this.store = {};

		/**
		 * @type {string}
		 */
		this.category = '';

		/**
		 * @type {string}
		 */
		this.currentDevice = '';

		/**
		 * @type {string[]}
		 */
		this.saleBrands = [];

		/**
		 * @type {boolean}
		 */
		this.isDesktop = false;

		/**
		 * @type {boolean}
		 */
		this.loading = true;

		/**
		 * @type {string}
		 */
		this.query = '';

		/**
		 * Used to pass the landing page url into the search service
		 * @type {string}
		 */
		this.url = '';

		// Create controllers
		this.lazyLoadController = new LazyLoadController(this, this.onLazy);
		this.salesBrandsController = new SalesBrandsController(this);
	}

	async connectedCallback () {
		super.connectedCallback();

		/**
		 * @type {SearchService}
		 */
		this.searchService = new SearchService({
			backgroundFacets: {
				...this.category
				? {
					// eslint-disable-next-line camelcase
					category_codes: [this.category]
				}
				: {}
			},
			clientGuid: window.env.HAWK_SEARCH_CLIENT_GUID,
			paginationSetCode: () => window?.matchMedia('(min-width: 475px)')?.matches ? '' : 'mobile'
		});

		// Create listener for GTM impressions
		this.searchService.onEvent('afterSearch', this.sendImpressions.bind(this));

		// Update category h1 tag if landing page
		if (this.url) {
			this.searchService.onEvent('afterStore', this.updateCategoryTitle.bind(this));
		}

		// Run initial search with parameters passed through
		const initialRequest = this.url
			? {
				url: this.url
			}
			: {
				query: this.query
			};
		await this.searchService.search(initialRequest);

		// Wait for service store to be fully loaded before setting initial value
		await this.searchService.store.initialized;
		this.store = this.searchService.store;

		// Set loading to false so templates can fully render
		this.loading = false;
	}

	/**
	 * @param {string} zoneName
	 */
	getZoneItems (zoneName) {
		return this.store?.state?.contentZones?.[zoneName]?.items || void 0;
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').SearchResponse} data
	 */
	sendImpressions (data) {
		this.gtm.hawkSearchSendImpressions(this.gtmList, this.gtmList, data?.results);
	}

	/**
	 * @param {import('extensions/hawksearch/services/search-service').SearchStore} store
	 */
	updateCategoryTitle (store) {
		// Find and update category title
		const categoryTitle = document.getElementById('js-category-header-title');

		if (categoryTitle && !window.mivaJS?.OmitHawkHeader) {
			categoryTitle.innerHTML = store?.state?.content?.heading;
		}

		// Find and update category description (if facets selected (excluding `price_sale`), remove description)
		const categoryDescription = document.getElementById('js-category-header-descrip');

		if (categoryDescription) {
			const filtedFacets = store?.state?.selectedFacets?.filter((selectedFacet) => selectedFacet.field !== 'price_sale');
			categoryDescription.innerHTML = filtedFacets?.length ? '' : store?.state?.content?.customHtml;
		}
	}

	/**
	 * Intersection observer callback
	 */
	async onLazy () {
		// Get sale brands
		this.saleBrands = await this.salesBrandsController.getSalesBrands();
	}

	/**
	 * BreakpointMixin callback
	 * @param {number} index
	 * @param {boolean} matches
	 */
	handleBreakpoint (index) {
		this.isDesktop = BreakpointLabels[index] === 'desktop';
		this.currentDevice = BreakpointLabels[index];
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').SearchResultsItem} result
	 * @param {Event} e
	 */
	handleResultClick (result, e) {
		if (result.type === 'product') {
			this.trackingService.trackSearchResultClick(result.id, result.url, this.store.state.trackingId, e);
		}
	}

	get gtmList () {
		return window.mivaJS?.Screen === 'CTGY' ? window.mivaJS.Category_Code : 'search';
	}

	renderResults () {
		return this.store?.state?.results?.map(result => {
			if (result?.type === 'inline-banner') {
				return this.renderInlineBanner(result.content);
			}

			return html`
				<x-card>
					<x-product-card
						@click="${e => this.handleResultClick(result, e)}"
						?hidden="${!result.visible}"
						brand="${result?.attributes?.brand}"
						code="${result.id}"
						?discounted-brand="${result?.attributes?.discounted_brands === 'yes'}"
						flag="${result?.attributes?.product_flag}"
						formatted-price="${result.formattedPrice}"
						formatted-retail="${result.formattedRetail}"
						gtm-list="${this.gtmList}"
						?has-metal-options="${result?.attributes?.has_metal_options}"
						hover-image-url="${result?.attributes?.product_image_two_resized || result?.attributes?.product_image_two}"
						image-url="${result?.imageUrl}"
						?incart-pricing="${result?.attributes?.incart_pricing}"
						name="${result.title}"
						price="${result.price}"
						retail="${result.retail}"
						sale-brands="${JSON.stringify(this.saleBrands)}"
						starting-at="${result?.attributes?.starting_at}"
						variant-swatch="${JSON.stringify(result?.attributes?.options_variant_swatch)}"
						url="${result.url}"
					></x-product-card>
				</x-card>
			`;
		});
	}

	/**
	 * @param {Event} e
	 */
	handlePerPage (e) {
		this.searchService.perPage(e.target.value);
	}

	/**
	 * @param {Event} e
	 */
	handleSortChange (e) {
		this.searchService.sort(e.target.value);
	}

	/**
	 * @param {Event} e
	 */
	handlePaginationChange (e) {
		const target = e.target;
		if (target.nodeName !== 'X-SEARCH-PAGINATION') {
			return false;
		}

		this.searchService.paginate(e.target.value);

		// Scroll to top after making pagination changes
		window.scrollTo({
			behavior: 'smooth',
			left: 0,
			top: 0
		});

		return true;
	}

	/**
	 * @param {string} field
	 * @param {string} value
	 */
	handleSelectedFacet (field, value) {
		this.searchService.removeFacetValue(field, value);
	}

	/**
	 * @typedef {Object} HandleFacetChangeOptions
	 * @prop {boolean} expandSelection
	 * @prop {string} field
	 * @prop {string} type
	 * @prop {string} value
	 * @prop {string} valueSelected
	 *
	 * @param {HandleFacetChangeOptions} options
	 */
	handleFacetChange ({expandSelection, field, type, value, valueSelected} = {}) {
		if (type === 'checkbox' || type === 'link') {
			if (expandSelection) {
				return this.searchService.toggleFacetValue(field, value);
			}

			if (valueSelected) {
				return this.searchService.clearFacet(field);
			}

			return this.searchService.setFacetValue(field, value);
		}

		return this.searchService.setFacetValue(field, value);
	}

	renderSelectedFacets () {
		const output = [];
		const selectedFacets = this.store?.state?.selectedFacets || [];

		for (let selectedFacet of selectedFacets) {
			const selectedFacetValues = selectedFacet?.values || [];

			for (let selectedFacetValue of selectedFacetValues) {
				if (selectedFacetValue.excluded) {
					continue;
				}

				output.push(html`
					<x-search-selected-facet
						@click="${() => this.handleSelectedFacet(selectedFacet.field, selectedFacetValue.value)}"
						?currency="${selectedFacet.currency}"
						label="${selectedFacet.title}"
						value="${selectedFacetValue.title}"
					></x-search-selected-facet>
				`);
			}
		}

		return output;
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').Facet} facet
	 */
	renderFacetValues (facet) {
		return facet?.values?.map(facetValue => html`
			<x-search-facet-value
				@change="${(e) => this.handleFacetChange({
					expandSelection: facet.expandSelection,
					field: facet.field,
					type: facet.type,
					value: e.target.value,
					valueSelected: facetValue.selected
				})}"
				?selected="${facetValue.selected}"
				type="${facet.type}"
				color="${facetValue.color?.hex}"
				image="${facetValue.imageUrl ?? facetValue?.color?.imageUrl}"
				count="${facetValue.count}"
				label="${facetValue?.color?.name ?? facetValue.title ?? facet.title}"
				value="${facetValue.value}"
				end="${facet.range?.end}"
				range-type="${facet.range?.type}"
				max="${facet.range?.max}"
				min="${facet.range?.min}"
				start="${facet.range?.start}"
				?show-inputs="${facet.range?.showInputs}"
				facet="${facet.field}"
			></x-search-facet-value>
		`);
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').Facet} facet
	 */
	renderFacetContent (facet) {
		const openToggleDetails = facet?.values?.slice(facet.truncation?.threshold)?.some(facetValue => facetValue.selected);

		if (facet.truncation?.threshold && (facet.type === 'checkbox' || facet.type === 'link')) {
			return html`<x-toggle-details limit="${facet.truncation?.threshold}" ?open="${openToggleDetails}">
				${this.renderFacetValues(facet)}
			</x-toggle-details>`;
		}

		return this.renderFacetValues(facet);
	}

	renderFacets () {
		return this.visibleFacets
			?.map(facet => {
				return html`
					<x-search-facet-title
						?open="${!facet.collapsed}"
						?collapsible="${facet.collapsible}"
						label="${facet.title}"
						tooltip="${facet.tooltip}"
						type="${facet.type}"
					>
						<x-search-facet type="${facet.type}">
							${this.renderFacetContent(facet)}
						</x-search-facet>
					</x-search-facet-title>
				`;
			});
	}

	renderFacetLayout () {
		return html`
			<h2 class="facets-header">
				<span>Customize Your Search</span>
				<button class="clear-facets-button" ?hidden="${!this.store?.state?.selectedFacets?.length}" @click="${() => this.searchService.clearFacets()}">
					<span class="icon-reset"></span>
					<span class="text">Reset Filters</span>
				</button>
			</h2>
			<div class="selected-facets" ?hidden="${!this.store?.state?.selectedFacets?.length}">
				${this.renderSelectedFacets()}
			</div>
			<div class="facets">
				${this.renderFacets()}
			</div>
		`;
	}

	renderLoader () {
		return html`<x-loader class="loader" aspect-ratio-desktop="1600/1200" aspect-ratio-tablet="820/1100" aspect-ratio="390/740"></x-loader>`;
	}

	renderMain () {
		return html`
			${
				this.isDesktop
					? html`
						<aside ?hidden="${!this.visibleFacets?.length}">
							<div class="sticky-outer">
								<div class="sticky">
									${this.renderFacetLayout()}
								</div>
							</div>
						</aside>
					`
					: nothing
			}
			<main ?hidden="${!this.store?.state?.results?.length}">
				<div class="controls">
					${
						this.isDesktop
							? nothing
							: html`
								<g-modal direction="left" header="false" has-padding="false">
									<button slot="trigger" class="mobile-facets-button">
										<span class="icon-svg-menu" aria-hidden="true"></span>
										Filters
									</button>
									<div slot="content" class="mobile-facets-container">
										${this.renderFacetLayout()}
									</div>
								</g-modal>
							`
					}

					<x-badge class="search-results-badge">
						<x-search-results-count
							page="${this.store?.state?.pagination?.page}"
							page-size="${this.store?.state?.pagination?.pageSize}"
							total="${this.store?.state?.pagination?.totalResults}"
						>
						</x-search-results-count>
					</x-badge>

					<div class="controls__right">
						<x-select label="Sort By" @change="${this.handleSortChange}" value="${this.store?.state?.sorting?.value}">
							${this.store?.state?.sorting?.options?.map(option => html`<option value="${option.value}" ?selected="${option.selected}">${option.title}</option>`)}
						</x-select>
					</div>
				</div>
				<div class="results">
					${this.renderResults()}
				</div>
				<div class="pagination">
					<x-search-pagination
						@change="${this.handlePaginationChange}"
						?first="${this.store?.state?.pagination?.displayFirstLink}"
						?last="${this.store?.state?.pagination?.displayLastLink}"
						max-pages="3"
						value="${this.store?.state?.pagination?.page}"
						total-pages="${this.store?.state?.pagination?.totalPages}"
					></x-search-pagination>
				</div>
			</main>
		`;
	}

	/**
	 * @param {string} name
	 * @param {string} content
	 */
	renderDynamicSlot (name, content) {
		// Remove existing slotted elements
		const foundSlots = this.querySelectorAll(`[slot="${name}"]`);
		if (!foundSlots?.length) {
			// Create wrapped slotted element
			const wrapperDiv = document.createElement('div');
			wrapperDiv.slot = name;
			wrapperDiv.innerHTML = content;
			wrapperDiv.classList.add('zone-slot');
			this.appendChild(wrapperDiv);
		}

		// Return slot to shadowdom
		return html`<slot name="${name}"></slot>`;
	}

	hasPageBuilderContent (content = '') {
		return content?.includes?.('x-page-builder');
	}

	/**
	 * @param {string} zoneName
	 * @param {boolean} slot
	 */
	renderContentZone (zoneName, slot = false) {

		/**
		 * @type {import('extensions/hawksearch/services/types').ContentZoneItem[]}
		 */
		const items = this.getZoneItems(zoneName);
		if (!items || this.store?.state?.pagination?.page > 1) {
			return nothing;
		}

		const content = [];

		for (const item of items) {
			// eslint-disable-next-line array-element-newline
			for (const [device, value] of Object.entries(item)) {
				if (device !== this.currentDevice) {
					continue;
				}

				switch (value.type) {
					case 'image':
						content.push(html`<x-banner
								@click="${() => this.handleBannerClick(value)}"
								@load="${() => this.handleBannerLoad(value)}"
								class="zone-device-${device}"
								device="${device}"
								url="${value.link?.url}"
								target="${value.link?.target}"
								image-url="${value.image?.url}"
								image-alt="${value.image?.altText}"
								image-title="${value.image?.title}"
								image-height="${value.image?.height}"
								image-width="${value.image?.width}"
							></x-banner>`);
						break;
					case 'custom':
						if (slot === true || this.hasPageBuilderContent(value.content)) {
							content.push(this.renderDynamicSlot(`zone-${value.zone}-${value.id}`, value.content));
						}
						else {
							content.push(html`<div class="zone-device-${device}">${unsafeHTML(value.content)}</div>`);
						}
						break;
					case 'custom-widget':
						if (slot === true || this.hasPageBuilderContent(value.data)) {
							content.push(this.renderDynamicSlot(`zone-${value.zone}-${value.id}`, value.data));
						}
						else {
							content.push(html`<div class="zone-device-${device}">${unsafeHTML(value.data)}</div>`);
						}
						break;
					case 'featured-items':
						content.push(html`<div class="zone-device-${device}">${this.renderFeaturedItemsWidget(value)}</div>`);
						break;
					case 'popular-queries':
						content.push(html`<div class="zone-device-${device}">${this.renderPopularQueries(value)}</div>`);
						break;
					default:
						break;
				}
			}
		}

		return html`<div class="zone zone-${zoneName}">${content}</div>`;
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').SearchResultsItem} product
	 * @param {Event} e
	 */
	handleFeaturedItemClick (product, e) {
		this.trackingService.trackSearchResultClick(product.id, product.url, this.store.state.trackingId, e);
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').ContentType} banner
	 */
	handleBannerClick (banner) {
		if (banner.trackingEnabled) {
			this.trackingService.trackBannerClick(banner.id, banner.campaignId, this.store?.state?.trackingId);
		}
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').ContentType} banner
	 */
	handleBannerLoad (banner) {
		if (banner.trackingEnabled) {
			this.trackingService.trackBannerImpression(banner.id, banner.campaignId, this.store?.state?.trackingId);
		}
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').FeaturedItemsContentItem[]} items
	 */
	renderFeaturedItems (items) {
		return items.map(product => {
			return html`
				<x-card>
					<x-product-card
						@click="${e => this.handleFeaturedItemClick(product, e)}"
						brand="${product?.attributes?.brand}"
						code="${product.id}"
						?discounted-brand="${product?.attributes?.discounted_brands === 'yes'}"
						flag="${product?.attributes?.product_flag}"
						formatted-price="${product.formattedPrice}"
						formatted-retail="${product.formattedRetail}"
						gtm-list="Featured Items"
						?has-metal-options="${product?.attributes?.has_metal_options}"
						hover-image-url="${product?.attributes?.product_image_two_resized || product?.attributes?.product_image_two}"
						image-url="${product?.imageUrl}"
						?incart-pricing="${product?.attributes?.incart_pricing}"
						name="${product.title}"
						price="${product.price}"
						retail="${product.retail}"
						sale-brands="${JSON.stringify(this.saleBrands)}"
						starting-at="${product?.attributes?.starting_at}"
						variant-swatch="${JSON.stringify(product?.attributes?.options_variant_swatch)}"
						url="${product.url}"
					></x-product-card>
				</x-card>
			`;
		});
	}

	renderFeaturedItemsWidget (data) {
		if (!data.items?.length) {
			return nothing;
		}

		return html`
			<div class="featured-items-container">
				<h2 class="featured-items-title">${data?.title}</h2>
				<x-carousel slides-per-view="5">
					${this.renderFeaturedItems(data.items)}
				</x-carousel>
			</div>
		`;
	}

	renderPopularQueries (data) {
		return html`
			<div class="query-suggestions-container">
				<h3 class="query-suggestions-header">Popular Searches</h3>
				<ul class="popular-query-suggestions">
					${data.items?.map(item => html`<li><a href="${this.searchService.joinUrl(window.mivaJS.urls.search, [[this.searchService.nonFacetQueryParams.query, item.query]])}">${item.query}</a></li>`)}
				</ul>
			</div>
		`;
	}

	render () {
		if (this.loading) {
			return this.renderLoader();
		}

		const containerClasses = {
			container: true,
			'is--category': this.url || this.category
		};

		const searchContainerClasses = {
			'search-container': true,
			'search-container--loading': !this.loading && this.store?.loading
		};

		return html`
			<div class="${classMap(containerClasses)}">
				<div class="custom-html-content">${this.renderContentZone('FeaturedLeftTop')}</div>

				<header ?hidden="${Boolean(this.url) || Boolean(this.category)}">
					<h1 class="query-header">
						${
							this.store?.state?.modifiedQuery || this.store?.state?.query
								? html`Searching for "${this.store?.state?.modifiedQuery || this.store?.state?.query}"`
								: html`All Results`
						}
					</h1>

					<div class="query-suggestions-container" ?hidden="${!this.store?.state?.querySuggestions?.length}">
						<h3 class="query-suggestions-header">Did you mean?</h3>
						<ul class="query-suggestions">
							${this.store?.state?.querySuggestions?.map(query => html`<li><a href="${this.searchService.joinUrl(window.mivaJS.urls.search, [[this.searchService.nonFacetQueryParams.query, query]])}">${query}</a></li>`)}
						</ul>
					</div>
				</header>

				<div class="custom-html-content">${this.renderContentZone('FeaturedLeftBottom')}</div>
				<div class="custom-html-content">${this.renderContentZone('FeaturedTop')}</div>

				<div class="${classMap(searchContainerClasses)}">
					${this.renderMain()}
				</div>

				<div class="custom-html-content">${this.renderContentZone('LeftBottom')}</div>

				${
					when(
						this.getZoneItems('FeaturedBottom'),
						() => html`
							<div class="custom-html-content">
								<div class="prod-ctgy-hdft-footer">
									<div class="about-banner-container">
										${this.renderContentZone('FeaturedBottom')}
									</div>
								</div>
							</div>
						`,
						() => nothing
					)
				}

				<div class="custom-html-content">${this.renderContentZone('FeaturedBottom2')}</div>

				${this.renderContentZone('PageBuilder', true)}
			</div>
		`;
	}

	renderInlineBanner (content) {
		return html`
			<div class="inline-banner">${this.renderContentZone(content.name)}</div>
		`;
	}

	updated () {
		if (this.renderRoot?.querySelectorAll('.about-banner-description').length > 0) {
			readMore('.about-banner-description', this.renderRoot);
		}
	}
};

defineCustomElement('x-search-app', SearchApp);
